import React from "react";
import { Badge } from "../../../../../ui/components/Badge";

export type KeyMetricGaugeChangeVariant = "up" | "down";

export interface KeyMetricGaugeCardProps {
  title: string;
  value: string;
  valueUnit: string;
  /** Percentage 0–100 used to fill the half-gauge. Omit or null = empty gauge. */
  percentage?: number | null;
  changeText?: string;
  changeVariant?: KeyMetricGaugeChangeVariant;
  badgeLabel?: string;
  formulaLabel?: string;
}

const changeVariantStyles: Record<KeyMetricGaugeChangeVariant, string> = {
  up: "text-success-600",
  down: "text-error-600",
};

/** Semicircle arc length for radius 80: π * 80 */
const SEMI_ARC_LENGTH = Math.PI * 80;

function getGaugeFillColor(percentage: number): string {
  if (percentage <= 50) return "#16a34a"; // green
  if (percentage <= 80) return "#d97706"; // amber
  return "#dc2626"; // red
}

/**
 * Key metric card with half-gauge (e.g. Expense ÷ Revenue). Gauge fills 0%–100%
 * by percentage; value/unit in center, 0%–100% labels, change text + Badge, formula.
 */
export function KeyMetricGaugeCard({
  title,
  value,
  valueUnit,
  percentage,
  changeText,
  changeVariant,
  badgeLabel,
  formulaLabel,
}: KeyMetricGaugeCardProps) {
  const pct = percentage != null ? Math.min(100, Math.max(0, percentage)) : 0;
  const fillLength = (pct / 100) * SEMI_ARC_LENGTH;
  const fillColor = getGaugeFillColor(pct);

  return (
    <div className="flex h-full min-h-0 flex-1 min-w-0 basis-0 flex-col items-start rounded-lg bg-white px-8 py-8 shadow-sm">
      <span className="text-caption font-caption text-neutral-400 uppercase tracking-wider">
        {title}
      </span>
      <div className="flex w-full flex-col items-center justify-center gap-2 py-6">
        <div className="flex h-32 w-full flex-none items-center justify-center relative">
          <svg
            className="h-32 w-full min-w-0 max-w-[280px] shrink-0"
            viewBox="0 0 200 100"
            preserveAspectRatio="xMidYMax meet"
            aria-hidden
          >
            {/* Track: full semicircle */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="12"
              strokeLinecap="round"
            />
            {/* Fill: from 0% to percentage */}
            {pct > 0 && (
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke={fillColor}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={`${fillLength} ${SEMI_ARC_LENGTH}`}
                strokeDashoffset={0}
              />
            )}
          </svg>
          <div className="flex flex-col items-center absolute bottom-0">
            <span className="font-['Inter'] text-[32px] font-[700] leading-[32px] text-neutral-900">
              {value}
            </span>
            <span className="text-caption font-caption text-neutral-400 uppercase">
              {valueUnit}
            </span>
          </div>
        </div>
        <div className="flex w-full items-center justify-between px-4">
          <span className="text-caption font-caption text-neutral-400">0%</span>
          <span className="text-caption font-caption text-neutral-400">100%</span>
        </div>
      </div>
      <div className="flex w-full flex-col items-center gap-2">
        <div className="flex items-center gap-2">
          {changeText && (
            <span
              className={`text-body font-body ${
                changeVariant
                  ? changeVariantStyles[changeVariant]
                  : "text-neutral-500"
              }`}
            >
              {changeText}
            </span>
          )}
          {badgeLabel && (
            <Badge variant="success">{badgeLabel}</Badge>
          )}
        </div>
        {formulaLabel && (
          <span className="text-caption font-caption text-neutral-400">
            {formulaLabel}
          </span>
        )}
      </div>
    </div>
  );
}
